import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchMenuLayout } from '../../store/menuLayoutSlice';
import { fetchSidebarTheme } from '../../store/sidebarThemeSlice';
import { useFeatureFlags } from '../../hooks/useFeatureEnabled';
import { getInFlightRequests } from '../../services/apiActivity';
import { isBootOverlayBlocked, subscribeBootOverlayBlocked } from '../../services/appBootState';
import { ONECARE_LOGO } from '../../config/branding';

/**
 * Full-screen loading screen shown while the platform boots.
 *
 * Why it exists: after login the app used to appear immediately and then
 * assemble itself in front of the user — the sidebar listed every module and
 * then removed the ones the user cannot see, the saved colours and menu order
 * arrived late, and each dashboard card popped in as its own request came back.
 * This overlay covers that whole assembly and lifts once the platform is ready.
 *
 * "Ready" means ALL of:
 *   1. the session has been confirmed with the server;
 *   2. the user's feature flags are loaded (they decide the entire menu);
 *   3. the saved menu layout and sidebar colours are loaded;
 *   4. the network has been quiet for QUIET_MS — no API request in flight —
 *      which is what tells us the first screen has stopped fetching.
 *
 * Safety valves:
 *   - it always lifts after MAX_WAIT_MS, so a hung request can never strand the
 *     user on a loading screen;
 *   - it shows once per login (and once per hospital switch), never on ordinary
 *     navigation;
 *   - it steps aside for the confidentiality-disclaimer modal.
 */

/** No request in flight for this long = the current screen finished loading. */
const QUIET_MS = 450;
/**
 * How far the bar has travelled once each step is done. Real milestones, not a
 * timer: the bar stops where the loading has actually got to. Between two
 * milestones it eases toward the one it is waiting on (see EASING below), so it
 * keeps creeping instead of freezing — and slows down as it approaches, which
 * is what makes a wait for a slow step read as "still working" rather than
 * "stuck". The last stretch is the settle period, which does run on time
 * because that step IS a duration.
 */
const PROGRESS_STOPS = {
  start: 5,
  session: 30,
  permissions: 60,
  menu: 72,
  colours: 84,
  settleFrom: 84,
  settleTo: 97,
};
/** Fraction of the remaining distance the bar covers each tick. */
const EASING = 0.16;
/** Never flash the loading screen for a few frames; keep it up at least this long. */
const MIN_SHOW_MS = 400;
/** Hard cap — reveal the app even if something never finishes. */
const MAX_WAIT_MS = 9000;
/** After this long, tell the user it's slower than usual instead of saying nothing. */
const SLOW_HINT_MS = 4500;
/** Fade-out duration; must match the CSS transition below. */
const FADE_MS = 280;
/** How often readiness is re-checked. */
const TICK_MS = 100;

/**
 * Identity (user + hospital) whose boot screen has already been shown. Module
 * level on purpose: navigating between pages must never re-trigger it, while a
 * new login or a hospital switch produces a new identity and a fresh screen.
 */
let bootedIdentity: string | null = null;

/** Routes that are public — no session, nothing to boot, so never cover them. */
const PUBLIC_PATH_PREFIXES = [
  '/onecare/login',
  '/onecare/platform/',
  '/onecare/contact',
  '/onecare/color-test',
  '/onecare/patients/',
  '/contact',
  '/demo-request',
  '/demo/setup/',
];

const isPublicPath = (pathname: string): boolean =>
  pathname === '/' ||
  pathname === '/onecare' ||
  pathname === '/onecare/' ||
  PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export const AppBootOverlay: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const userId = useAppSelector((state) => state.auth.user?.id ?? null);
  const hospitalId = useAppSelector((state) => state.auth.hospital?.id ?? null);
  const validationState = useAppSelector((state) => state.auth.validationState);
  const networkError = useAppSelector((state) => state.auth.networkError);
  const menuLayoutStatus = useAppSelector((state) => state.menuLayout.status);
  const sidebarThemeStatus = useAppSelector((state) => state.sidebarTheme.status);
  const { isLoading: featureFlagsLoading } = useFeatureFlags({ enabled: isAuthenticated });

  const identity = isAuthenticated && userId !== null ? `${userId}:${hospitalId ?? 'none'}` : null;

  const [revealedIdentity, setRevealedIdentity] = useState<string | null>(bootedIdentity);
  const [fading, setFading] = useState(false);
  const [slow, setSlow] = useState(false);
  const [blocked, setBlocked] = useState<boolean>(() => isBootOverlayBlocked());

  const active =
    identity !== null && !isPublicPath(location.pathname) && revealedIdentity !== identity;

  // --- readiness inputs, mirrored into refs so the timer below can read the
  //     latest values without being torn down and recreated every render ---
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const authReady =
    validationState === 'valid' || validationState === 'failed' || networkError || isOffline;
  const layoutReady = menuLayoutStatus === 'loaded' || menuLayoutStatus === 'error';
  const themeReady = sidebarThemeStatus === 'loaded' || sidebarThemeStatus === 'error';
  const shellReady = authReady && !featureFlagsLoading && layoutReady && themeReady;

  const [progress, setProgress] = useState(PROGRESS_STOPS.start);

  const shellReadyRef = useRef(shellReady);
  const readinessRef = useRef({ authReady, featureFlagsLoading, layoutReady, themeReady });
  const blockedRef = useRef(blocked);
  // `undefined` = "no identity observed yet", so the effect below also runs on
  // the very first mount (a real identity or `null` are both real values).
  const identityRef = useRef<string | null | undefined>(undefined);
  const startedAtRef = useRef<number>(Date.now());
  const lastBusyAtRef = useRef<number>(Date.now());
  const fadingRef = useRef(false);

  useEffect(() => {
    shellReadyRef.current = shellReady;
    readinessRef.current = { authReady, featureFlagsLoading, layoutReady, themeReady };
  }, [shellReady, authReady, featureFlagsLoading, layoutReady, themeReady]);

  useEffect(() => {
    blockedRef.current = blocked;
  }, [blocked]);

  // A blocking dialog closed — restart both timers so the user gets a clean
  // boot window afterwards instead of an instantly-expired one.
  useEffect(() => {
    if (!blocked) {
      startedAtRef.current = Date.now();
      lastBusyAtRef.current = Date.now();
    }
  }, [blocked]);

  useEffect(() => subscribeBootOverlayBlocked(() => setBlocked(isBootOverlayBlocked())), []);

  // New login / hospital switch: start a fresh boot window.
  useEffect(() => {
    if (identityRef.current !== identity) {
      identityRef.current = identity;
      startedAtRef.current = Date.now();
      lastBusyAtRef.current = Date.now();
      fadingRef.current = false;
      setFading(false);
      setSlow(false);
      setProgress(PROGRESS_STOPS.start);

      // Signed out. Forget that we have booted, so the next sign-in gets a
      // fresh loading screen — even for the same person in the same tab, whose
      // menu layout, colours and feature flags were all thrown away on logout.
      if (identity === null) {
        bootedIdentity = null;
        setRevealedIdentity(null);
      }
    }
  }, [identity]);

  // The sidebar normally kicks these off, but it may not be mounted yet (or at
  // all, on sidebar-less pages). Fetching them here means readiness never waits
  // on a component that never appears.
  useEffect(() => {
    if (!active || !authReady) return;
    if (menuLayoutStatus === 'idle') {
      dispatch(fetchMenuLayout());
    }
    if (sidebarThemeStatus === 'idle') {
      dispatch(fetchSidebarTheme());
    }
  }, [active, authReady, menuLayoutStatus, sidebarThemeStatus, dispatch]);

  const reveal = useCallback(() => {
    if (fadingRef.current) return;
    // Captured now: if the user switches hospital mid-fade, the effect above
    // has already started a fresh boot window and this timer must not mark the
    // NEW identity as booted.
    const bootingIdentity = identityRef.current ?? null;
    fadingRef.current = true;
    setFading(true);
    window.setTimeout(() => {
      if (identityRef.current !== bootingIdentity) return;
      bootedIdentity = bootingIdentity;
      setRevealedIdentity(bootingIdentity);
      setFading(false);
      fadingRef.current = false;
    }, FADE_MS);
  }, []);

  useEffect(() => {
    if (!active) return;

    const timer = window.setInterval(() => {
      const now = Date.now();

      // A dialog is up: pause everything. The user is answering it, not waiting
      // on us, and the boot window should not expire behind the dialog.
      if (blockedRef.current) {
        startedAtRef.current = now;
        lastBusyAtRef.current = now;
        return;
      }

      // The quiet period only counts once the shell itself is ready — otherwise
      // a lull before the next wave of requests would look like "all done".
      if (!shellReadyRef.current || getInFlightRequests() > 0) {
        lastBusyAtRef.current = now;
      }

      const elapsed = now - startedAtRef.current;
      if (elapsed >= SLOW_HINT_MS) {
        setSlow(true);
      }

      const quietFor = now - lastBusyAtRef.current;
      const everythingLoaded =
        shellReadyRef.current && quietFor >= QUIET_MS && elapsed >= MIN_SHOW_MS;

      if (everythingLoaded || elapsed >= MAX_WAIT_MS) {
        setProgress(100);
        reveal();
        return;
      }

      // Where the loading has actually got to. Each completed step raises the
      // ceiling; the bar then eases toward it, so it always moves without ever
      // claiming a step is done before it is.
      // The ceiling is the step being waited on RIGHT NOW, and the steps are
      // strictly sequential — a later one being ready must never pull the bar
      // past an earlier one that isn't. (The theme often loads from its local
      // cache long before the permissions do.)
      const { authReady: auth, featureFlagsLoading: flagsLoading, layoutReady: layout } =
        readinessRef.current;
      let ceiling = PROGRESS_STOPS.session;
      if (auth) ceiling = PROGRESS_STOPS.permissions;
      if (auth && !flagsLoading) ceiling = PROGRESS_STOPS.menu;
      if (auth && !flagsLoading && layout) ceiling = PROGRESS_STOPS.colours;
      if (shellReadyRef.current) {
        // Everything is in; the only thing left IS a fixed settle period, so
        // this last stretch is honestly time-based.
        const settled = Math.min(1, quietFor / QUIET_MS);
        ceiling =
          PROGRESS_STOPS.settleFrom +
          (PROGRESS_STOPS.settleTo - PROGRESS_STOPS.settleFrom) * settled;
      }

      setProgress((current) => Math.max(current, current + (ceiling - current) * EASING));
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, [active, reveal]);

  if (!active || blocked) return null;

  // Names the step the bar is currently waiting on, so the number has a meaning
  // the user can see rather than being a bar that moves on its own.
  const stepLabel = !authReady
    ? 'Checking your session'
    : featureFlagsLoading
      ? 'Loading your permissions'
      : !layoutReady
        ? 'Loading your menu'
        : !themeReady
          ? 'Applying your theme'
          : 'Preparing your workspace';

  const percent = Math.round(progress);

  return (
    <div
      className={`dashboard-bg-pattern fixed inset-0 flex flex-col items-center justify-center gap-6 transition-opacity duration-300 ${
        fading ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
      style={{ zIndex: 100000 }}
      data-testid="app-boot-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <img
        src={ONECARE_LOGO}
        alt=""
        aria-hidden="true"
        className="h-28 w-auto object-contain sm:h-36"
      />

      <div className="flex w-56 flex-col gap-2">
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label="Loading the platform"
        >
          <div
            className="boot-progress-fill h-full rounded-full bg-blue-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">{stepLabel}</span>
          <span className="text-xs font-semibold tabular-nums text-gray-500">{percent}%</span>
        </div>
      </div>

      {slow && (
        <p className="text-xs text-gray-600">This is taking longer than usual</p>
      )}
    </div>
  );
};

export default AppBootOverlay;
